/**
 * Privacy-safe Datadog RUM setup. Inert unless both application credentials
 * are configured.
 */

// Slim excludes Session Replay and profiling at the package level. Replay must
// not be available in an app that renders prescriptions, visits and lab data.
import { datadogRum, RumEvent } from "@datadog/browser-rum-slim";
import { reactPlugin } from "@datadog/browser-rum-react";

import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_ENVIRONMENT,
  DATADOG_VERSION,
} from "@modules/common/constants";

/**
 * Keeps only the origin because URL paths and asset names can contain
 * medication, account and order details. Non-hierarchical URLs retain only
 * their protocol; malformed URLs are replaced.
 */
export function sanitizeUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    return url.origin === "null" ? url.protocol : url.origin;
  } catch {
    return "redacted:";
  }
}

// beforeSend requires in-place mutation to alter an event.
/* eslint-disable no-param-reassign */
function beforeSend(event: RumEvent): boolean {
  // Ignore expected document 404s caused by Amplify's SPA rewrite.
  if (
    event.type === "resource" &&
    event.resource.type === "document" &&
    event.resource.status_code === 404
  ) {
    return false;
  }

  event.view.url = sanitizeUrl(event.view.url);

  if (event.view.referrer) {
    event.view.referrer = sanitizeUrl(event.view.referrer);
  }

  if (event.type === "resource") {
    event.resource.url = sanitizeUrl(event.resource.url);
  }

  return true;
}
/* eslint-enable no-param-reassign */

let initialized = false;

/** Initializes RUM once when both application credentials are configured. */
export function initDatadog(): void {
  if (initialized || !DATADOG_APPLICATION_ID || !DATADOG_CLIENT_TOKEN) {
    return;
  }

  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: "datadoghq.com",
    service: "healthcare-client",
    env: DATADOG_ENVIRONMENT,
    version: DATADOG_VERSION,
    sessionSampleRate: 100,
    // Never attach a Noom identity; use Datadog's random browser ID only.
    trackAnonymousUser: true,

    // Explicit even though the slim package cannot record replay.
    sessionReplaySampleRate: 0,
    // Must remain explicit; SDK v7 otherwise defaults to "mask-user-input".
    defaultPrivacyLevel: "mask",

    // Automatic action names could contain medication or treatment text.
    trackUserInteractions: false,

    trackResources: true,
    trackLongTasks: true,

    // Name views by route pattern rather than identifier-bearing raw paths.
    // Requires Datadog's Routes wrapper in App.tsx.
    plugins: [reactPlugin({ router: true })],

    beforeSend,
  });

  initialized = true;
}

/** Records a custom operational action. */
export function trackDatadogAction(name: string, context?: object): void {
  datadogRum.addAction(name, context);
}

/** Records an error that was handled and would not surface to RUM on its own. */
export function trackDatadogError(error: Error, context?: object): void {
  datadogRum.addError(error, context);
}
